// @ts-strict-ignore
import { Dictionary } from 'typescript-collections';
import { ArticleInfoVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { LoadConnectionTextureProcessor } from '@3d-scene/processor/load-connection-texture.processor';
import { Injectable } from '@angular/core';
import { ArticleChangedService } from '@3d-scene/service/article-changed.service';

/**
 * Manager created in addition to ArticleContainerLoader to perform delayed/postponed executions related to articles.
 * E.g. in case new complex article with parts which are dynamic articles (label-strip, busbar) need to be created,
 * connections on such dynamic articles need to be created (and textures need to be generated) afterwards after complete creation
 * of ArticleContainerWrapper ('articlesWrapperCreated' event).
 */
@Injectable({ providedIn: 'root' })
export class ArticleLoadingManager {

  private _usageIdToArticleInfo: Dictionary<string, ArticleInfoVO> = new Dictionary<string, ArticleInfoVO>();

  constructor(private projectModel: ProjectModel, private loadConnectionTextureProcessor: LoadConnectionTextureProcessor, private _articleChangedService: ArticleChangedService) {}

  public storePendingArticle(container: ArticleContainer, articleInfo: ArticleInfoVO): void {
    if (!container) {
      return;
    }

    this._usageIdToArticleInfo.setValue(container.articleGUID, articleInfo);
  }

  public isPendingArticle(container: ArticleContainer): boolean {
    if (!container) {
      return false;
    }

    return this._usageIdToArticleInfo.containsKey(container.articleGUID);
  }

  public getPendingArticleInfo(articleGuid: string): ArticleInfoVO {
    if (!articleGuid) {
      return null;
    }

    return this._usageIdToArticleInfo.getValue(articleGuid);
  }

  // TODO: (in flex also) in case when adding of new (complex) article failed (e.g. not enough space) some information is not cleared
  // in flex: ProjectLoader created in ArticleContainersWrapper is not removed from ProjectLoaderHolder
  // article usages created in ArticleContainersWrapper are not removed from MainModel.articleUsageCache
  // in html: pending articles are not removed from ArticleLoadingManager
  // article usages are not removed from ProjectModel.articleUsageCache
  public async checkLoadedArticlesStatus(data: any): Promise<void> {
    const container: ArticleContainer = data.container;
    if (container && this.isPendingArticle(container)) {
      this.dispatchArticleChangeMessage(container);
      this.projectModel.addArticleInfoToCurrentProject(this._usageIdToArticleInfo.getValue(container.articleGUID));
      this._usageIdToArticleInfo.remove(container.articleGUID);

      await this.applyTexturesToFinalizedArticles([container]);
    }
  }

  public dispatchArticleChangeMessage(container: ArticleContainer, updateTextures: boolean = true): void {
    this._articleChangedService.articleChanged(container.articleGUID, updateTextures);
  }

  public async applyTexturesToFinalizedArticles(articleContainers: ArticleContainer[]): Promise<void> {
    if (!articleContainers) {
      return null;
    }

    const finalizedContainers: ArticleContainer[] = articleContainers.filter((container: ArticleContainer) => !this.isPendingArticle(container));
    return this.loadConnectionTextureProcessor.loadAndApplyTexturesToArticleContainersObservable(finalizedContainers).toPromise();
  }

  public clear(): void {
    this._usageIdToArticleInfo.clear();
  }
}
